/**
 * FinancialAnalysisPage.js - Análise Financeira Real
 * BLOCO 3: Controlo de Lucro e Stock
 * 
 * Funcionalidades:
 * - Histórico de Fornecedores & Preços
 * - Controlo de Lucro Real
 * - Rotação de Stock (FIFO)
 * - Produtos Sem Saída
 */

import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  TrendingUp, TrendingDown, Package, AlertTriangle,
  BarChart3, PieChart, Store, Truck, Clock, CheckCircle, Users
} from 'lucide-react';
import { formatCurrency, cn } from '@/lib/utils';
import GlobalFilters from '@/components/GlobalFilters';
import { EmptyStatePanel } from '@/components/common/EmptyStatePanel';
import { ModuleStateBoundary } from '@/components/common/ModuleStateBoundary';
import { ModulePageHero } from '@/components/common/ModulePageHero';
import { useGlobalFilters } from '@/hooks/useGlobalFilters';
import { useFetchTelemetry } from '@/hooks/useFetchTelemetry';
import { safeRequest } from '@/lib/safeRequest';
import { expensesAPI, catalogsAPI } from '@/lib/api';
import { getStoredAuthToken } from '@/lib/authStorage';
import { useLanguage } from '@/i18n';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: API_BASE_URL });
api.interceptors.request.use(config => {
  const token = getStoredAuthToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const financialAPI = {
  // Supplier Analysis
  getSupplierPrices: (productId) => api.get(`/api/financial-analysis/supplier-prices/${productId}`),
  compareSuppliers: () => api.get('/api/financial-analysis/supplier-comparison'),
  
  // Profit Analysis
  getProfitByProduct: (params) => api.get('/api/financial-analysis/profit/by-product', { params }),
  getProfitByService: (params) => api.get('/api/financial-analysis/profit/by-service', { params }),
  getProfitByLocation: (params) => api.get('/api/financial-analysis/profit/by-location', { params }),
  getProfitByEmployee: (params) => api.get('/api/financial-analysis/profit/by-employee', { params }),
  
  // Stock Analysis
  getStockRotation: (params) => api.get('/api/financial-analysis/stock/rotation', { params }),
  getStaleStock: (params) => api.get('/api/financial-analysis/stock/stale', { params }),
  getStockAlerts: () => api.get('/api/financial-analysis/stock/alerts'),
  getCapitalTied: (params) => api.get('/api/financial-analysis/stock/capital-tied', { params }),
  
  // Margin Analysis
  getMarginAnalysis: (params) => api.get('/api/financial-analysis/margin-analysis', { params }),
  getDetailedIndicators: (params) => api.get('/api/financial-analysis/detailed-indicators', { params })
};

const ERP_CARD = 'rounded-sm border border-slate-200 bg-white shadow-none';
const ERP_INSET = 'rounded-sm border border-slate-200 bg-slate-50';
const ERP_NUM = 'font-mono tabular-nums tracking-tight';
const ERP_LABEL = 'text-[10px] uppercase tracking-[0.16em] text-slate-500';
const ERP_ROW = 'flex items-center justify-between gap-3 rounded-sm border border-slate-200 bg-slate-50 px-2.5 py-1.5';
const ERP_VALUE_CLAMP = 'font-mono tabular-nums tracking-tight text-[clamp(0.82rem,0.62rem+0.35vw,1.05rem)]';

export default function FinancialAnalysisPage() {
  const { t, language } = useLanguage();
  const [activeTab, setActiveTab] = useState('profit');
  const [loading, setLoading] = useState(true);
  const [minimumMarginPct, setMinimumMarginPct] = useState(() => {
    const stored = window.localStorage.getItem('financial-analysis-minimum-margin-pct');
    return stored ? Number(stored) : 20;
  });
  
  // Data states
  const [profitByProduct, setProfitByProduct] = useState(null);
  const [profitByService, setProfitByService] = useState(null);
  const [profitByLocation, setProfitByLocation] = useState(null);
  const [profitByEmployee, setProfitByEmployee] = useState(null);
  const [supplierComparison, setSupplierComparison] = useState([]);
  const [stockRotation, setStockRotation] = useState(null);
  const [staleStock, setStaleStock] = useState(null);
  const [stockAlerts, setStockAlerts] = useState(null);
  const [capitalTied, setCapitalTied] = useState(null);
  const [marginAnalysis, setMarginAnalysis] = useState(null);
  const [detailedIndicators, setDetailedIndicators] = useState(null);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [expensePgtAnalytics, setExpensePgtAnalytics] = useState(null);
  const [expenseCategoryFilter, setExpenseCategoryFilter] = useState('all');
  const [expenseSubcategoryFilter, setExpenseSubcategoryFilter] = useState('all');
  const loadDataRef = useRef(null);
  const { partialIssues, captureFetchTelemetry, resetFetchTelemetry } = useFetchTelemetry();
  
  const { filters, setFilters } = useGlobalFilters({
    period: 'all',
    location_id: null
  });

  const formatMaputoDate = (value) => {
    if (!value) return t('financialAnalysis.noData');
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return t('financialAnalysis.noData');
    return new Intl.DateTimeFormat(language === 'pt' ? 'pt-MZ' : 'en-GB', {
      timeZone: 'Africa/Maputo',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(parsed);
  };

  useEffect(() => {
    loadDataRef.current?.();
  }, [filters, minimumMarginPct, expenseCategoryFilter, expenseSubcategoryFilter]);

  useEffect(() => {
    if (expenseCategoryFilter === 'all' && expenseSubcategoryFilter !== 'all') {
      setExpenseSubcategoryFilter('all');
    }
  }, [expenseCategoryFilter, expenseSubcategoryFilter]);

  useEffect(() => {
    window.localStorage.setItem('financial-analysis-minimum-margin-pct', String(minimumMarginPct));
  }, [minimumMarginPct]);

  const loadData = async () => {
    const failedRequests = [];
    try {
      setLoading(true);
      resetFetchTelemetry();
      const params = {
        period: filters.period,
        location_id: filters.location_id,
        employee_id: filters.employee_id,
        minimum_margin_pct: minimumMarginPct,
      };
      
      const expenseAnalyticsParams = {
        period: filters.period,
        location_id: filters.location_id,
        analysis_scope: 'financial',
      };
      if (expenseCategoryFilter !== 'all') expenseAnalyticsParams.category_id = expenseCategoryFilter;
      if (expenseSubcategoryFilter !== 'all') expenseAnalyticsParams.subcategory_id = expenseSubcategoryFilter;

      const [productRes, serviceRes, locationRes, employeeRes, suppliersRes, rotationRes, staleRes, alertsRes, capitalRes, marginRes, indicatorsRes, expenseCategoriesRes, expenseAnalyticsRes] = await Promise.all([
        safeRequest(() => financialAPI.getProfitByProduct(params), { data: { products: [], totals: {}, warnings: [] } }, { onError: () => failedRequests.push('profit-by-product'), telemetry: { module: 'financial-analysis', endpoint: 'profit.by-product', onEvent: captureFetchTelemetry } }),
        safeRequest(() => financialAPI.getProfitByService(params), { data: { totals: {}, expense_allocation: { allocated_total_expenses: {} } } }, { onError: () => failedRequests.push('profit-by-service'), telemetry: { module: 'financial-analysis', endpoint: 'profit.by-service', onEvent: captureFetchTelemetry } }),
        safeRequest(() => financialAPI.getProfitByLocation(params), { data: { totals: {}, locations: [], warnings: {} } }, { onError: () => failedRequests.push('profit-by-location'), telemetry: { module: 'financial-analysis', endpoint: 'profit.by-location', onEvent: captureFetchTelemetry } }),
        safeRequest(() => financialAPI.getProfitByEmployee(params), { data: { employees: [], totals: {} } }, { onError: () => failedRequests.push('profit-by-employee'), telemetry: { module: 'financial-analysis', endpoint: 'profit.by-employee', onEvent: captureFetchTelemetry } }),
        safeRequest(() => financialAPI.compareSuppliers(), { data: [] }, { onError: () => failedRequests.push('supplier-comparison'), telemetry: { module: 'financial-analysis', endpoint: 'supplier.comparison', onEvent: captureFetchTelemetry } }),
        safeRequest(() => financialAPI.getStockRotation({ period: 'last_3_months', location_id: filters.location_id }), { data: { high_rotation_products: [], high_rotation_count: 0, low_rotation_products: [], low_rotation_count: 0 } }, { onError: () => failedRequests.push('stock-rotation'), telemetry: { module: 'financial-analysis', endpoint: 'stock.rotation', onEvent: captureFetchTelemetry } }),
        safeRequest(() => financialAPI.getStaleStock({ days_without_sale: 30, location_id: filters.location_id }), { data: { stale_products: [], total_stale_products: 0, total_stale_value: 0, recommendations: [], summary: {} } }, { onError: () => failedRequests.push('stale-stock'), telemetry: { module: 'financial-analysis', endpoint: 'stock.stale', onEvent: captureFetchTelemetry } }),
        safeRequest(() => financialAPI.getStockAlerts(), { data: { alerts: [], by_priority: { critical: 0, high: 0, medium: 0 }, summary: {}, total_alerts: 0 } }, { onError: () => failedRequests.push('stock-alerts'), telemetry: { module: 'financial-analysis', endpoint: 'stock.alerts', onEvent: captureFetchTelemetry } }),
        safeRequest(() => financialAPI.getCapitalTied({ location_id: filters.location_id }), { data: { summary: { total_value_at_cost: 0, total_units: 0 }, items: [], total_capital_tied: 0 } }, { onError: () => failedRequests.push('capital-tied'), telemetry: { module: 'financial-analysis', endpoint: 'stock.capital-tied', onEvent: captureFetchTelemetry } }),
        safeRequest(() => financialAPI.getMarginAnalysis(params), { data: { products: [], summary: {}, configuration: { minimum_margin_pct: minimumMarginPct }, underperforming_count: 0 } }, { onError: () => failedRequests.push('margin-analysis'), telemetry: { module: 'financial-analysis', endpoint: 'margin.analysis', onEvent: captureFetchTelemetry } }),
        safeRequest(() => financialAPI.getDetailedIndicators(params), { data: { current: {}, comparison: {}, highlights: {}, monthly_trend: [] } }, { onError: () => failedRequests.push('detailed-indicators'), telemetry: { module: 'financial-analysis', endpoint: 'detailed-indicators', onEvent: captureFetchTelemetry } }),
        safeRequest(() => catalogsAPI.listExpenseCategories(), { data: [] }, { onError: () => failedRequests.push('expense-categories') }),
        safeRequest(() => expensesAPI.getPgtAnalytics(expenseAnalyticsParams), { data: { summary: {}, by_category: [], by_subcategory: [] } }, { onError: () => failedRequests.push('expense-pgt-analytics') }),
      ]);
      
      setProfitByProduct(productRes.data);
      setProfitByService(serviceRes.data);
      setProfitByLocation(locationRes.data);
      setProfitByEmployee(employeeRes.data);
      setSupplierComparison(suppliersRes.data || []);
      setStockRotation(rotationRes.data);
      setStaleStock(staleRes.data);
      setStockAlerts(alertsRes.data);
      setCapitalTied(capitalRes.data);
      setMarginAnalysis(marginRes.data);
      setDetailedIndicators(indicatorsRes.data);
      setExpenseCategories(Array.isArray(expenseCategoriesRes.data) ? expenseCategoriesRes.data : expenseCategoriesRes.data || []);
      setExpensePgtAnalytics(expenseAnalyticsRes.data || null);
      if (failedRequests.length) {
        toast.info(t('financialAnalysis.partialPanelsToast'));
      }
    } catch (error) {
      console.error('Failed to load financial data:', error);
      toast.error(t('financialAnalysis.loadError'));
    } finally {
      setLoading(false);
    }
  };

  loadDataRef.current = loadData;

  const locationTotals = profitByLocation?.totals || {};
  const indicatorsCurrent = detailedIndicators?.current || {};
  const indicatorsComparison = detailedIndicators?.comparison || {};
  const indicatorsHighlights = detailedIndicators?.highlights || {};
  const monthlyTrend = detailedIndicators?.monthly_trend || [];
  const topLevelExpenseCategories = expenseCategories.filter((category) => !category.parent_id);
  const expenseSubcategoryOptions = expenseCategoryFilter === 'all'
    ? []
    : (topLevelExpenseCategories.find((category) => category.id === expenseCategoryFilter)?.subcategories || []);

  useEffect(() => {
    if (expenseSubcategoryFilter !== 'all' && !expenseSubcategoryOptions.some((subcategory) => subcategory.id === expenseSubcategoryFilter)) {
      setExpenseSubcategoryFilter('all');
    }
  }, [expenseSubcategoryFilter, expenseSubcategoryOptions]);
  const maxMonthlyRevenue = Math.max(...monthlyTrend.map((item) => item.revenue || 0), 1);
  const profitabilitySignal = indicatorsHighlights.profitability_signal || 'healthy';
  const profitabilitySignalLabel = {
    healthy: t('financialAnalysis.signalHealthy'),
    warning: t('financialAnalysis.signalWarning'),
    critical: t('financialAnalysis.signalCritical'),
  }[profitabilitySignal] || profitabilitySignal;
  const stockAlertTotal = stockAlerts?.total_alerts ?? stockAlerts?.summary?.total_alerts ?? (stockAlerts?.alerts || []).length;
  const stockAlertByPriority = stockAlerts?.by_priority || stockAlerts?.summary?.by_priority || {};
  const moduleAllocationCards = [
    { key: 'commercial', label: t('financialAnalysis.moduleCommercial'), data: profitByService?.commercial, accent: 'blue' },
    { key: 'projects', label: t('financialAnalysis.moduleProjects'), data: profitByService?.projects, accent: 'amber' },
    { key: 'repairs', label: t('financialAnalysis.moduleRepairs'), data: profitByService?.repairs, accent: 'orange' },
    { key: 'kits', label: t('financialAnalysis.moduleKits'), data: profitByService?.kits, accent: 'emerald' },
    { key: 'gaming', label: t('financialAnalysis.moduleGaming'), data: profitByService?.gaming, accent: 'purple' },
    { key: 'papelaria', label: t('financialAnalysis.modulePapelaria'), data: profitByService?.papelaria, accent: 'sky' },
  ];

  const renderDeltaBadge = (value, suffix = '%') => {
    const numericValue = Number(value || 0);
    const positive = numericValue >= 0;
    const Icon = positive ? TrendingUp : TrendingDown;
    return (
      <Badge className={cn('gap-1 rounded-sm border px-1.5 py-0 text-[10px] font-medium', positive ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-red-200 bg-red-50 text-red-700')}>
        <Icon className="h-3 w-3" />
        {positive ? '+' : ''}{numericValue.toFixed(1)}{suffix}
      </Badge>
    );
  };

  const financialHeroStats = [
    {
      label: t('financialAnalysis.totalRevenue'),
      value: formatCurrency(locationTotals.total_revenue || 0),
      helper: t('financialAnalysis.quickComparisonsDescription'),
    },
    {
      label: t('financialAnalysis.totalCostExpenses'),
      value: formatCurrency(locationTotals.total_cost || 0),
      helper: `${t('financialAnalysis.expensesAndCharges')}: ${formatCurrency(locationTotals.expenses || 0)}`,
    },
    {
      label: t('financialAnalysis.operationalProfit'),
      value: formatCurrency(locationTotals.total_profit || 0),
      helper: t('financialAnalysis.profitComplianceDescription'),
    },
    {
      label: t('financialAnalysis.staleStock'),
      value: formatCurrency(staleStock?.total_stale_value || 0),
      helper: t('financialAnalysis.productsCount', { count: staleStock?.total_stale_products || 0 }),
    },
  ];

  const hasFinancialData = Boolean(
    (profitByProduct?.products || profitByProduct?.items || []).length ||
    (supplierComparison || []).length ||
    (monthlyTrend || []).length ||
    staleStock?.total_stale_products ||
    stockAlerts?.alerts?.length ||
    (profitByEmployee?.employees || []).length ||
    (profitByLocation?.locations || []).length ||
    Object.keys(locationTotals || {}).length
  );

  return (
    <ModuleStateBoundary
      loading={loading}
      hasData={hasFinancialData}
      partialIssues={partialIssues}
      loadingTestId="financial-analysis-loading-state"
      partialIssuesTestId="financial-analysis-partial-issues"
      emptyState={
        <div className="bg-slate-100 p-3 space-y-3" data-testid="financial-analysis-page">
          <EmptyStatePanel
            title={t('financialAnalysis.emptyTitle')}
            description={t('financialAnalysis.emptyDescription')}
            actionLabel={t('actions.refresh')}
            onAction={loadData}
            testId="financial-analysis-empty-state"
          />
        </div>
      }
    >
    <div className="bg-slate-100 p-3 space-y-3" data-testid="financial-analysis-page">
      <ModulePageHero
        title={t('financialAnalysis.title')}
        description={t('financialAnalysis.description')}
        icon={BarChart3}
        badge={t('financialAnalysis.badge')}
        stats={financialHeroStats}
        compact
        className="rounded-sm border-slate-200 shadow-none"
        testId="financial-analysis-hero"
      />

      {/* Global Filters */}
      <GlobalFilters 
        filters={['period', 'location', 'employee']}
        onChange={(newFilters) => setFilters(newFilters)}
        defaultPeriod={filters.period}
        compact
        className="rounded-sm border border-slate-200 bg-white p-2"
        filterTriggerClassName="h-7 rounded-sm text-[11px]"
        actionButtonClassName="h-7 rounded-sm text-[11px]"
        activeBadgeClassName="rounded-sm px-1.5 py-0 text-[10px]"
      />

      {/* Summary KPIs */}
      <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-[minmax(0,1.6fr)_repeat(5,minmax(0,0.78fr))] 2xl:grid-cols-[minmax(0,1.8fr)_repeat(5,minmax(0,0.78fr))]">
        <Card className="rounded-sm border-emerald-200 bg-emerald-50/70 shadow-none xl:min-w-0">
          <CardHeader className="pb-1.5 pt-2.5">
            <CardTitle className="flex items-center gap-1.5 text-sm font-semibold tracking-tight"><CheckCircle className="h-3.5 w-3.5 text-emerald-700" /> {t('financialAnalysis.profitCompliance')}</CardTitle>
            <CardDescription className="text-[11px] leading-snug">
              {t('financialAnalysis.profitComplianceDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-2 pb-2.5 md:grid-cols-3 xl:grid-cols-3">
            <div className="rounded-sm border border-white/80 bg-white p-2">
              <p className={ERP_LABEL}>{t('financialAnalysis.expensesAndCharges')}</p>
              <p className={cn('mt-1 font-semibold text-slate-900', ERP_VALUE_CLAMP)}>{formatCurrency(locationTotals.expenses || 0)}</p>
            </div>
            <div className="rounded-sm border border-white/80 bg-white p-2">
              <p className={ERP_LABEL}>{t('financialAnalysis.operatingMargin')}</p>
              <p className={cn('mt-1 font-semibold text-slate-900', ERP_VALUE_CLAMP)}>{Number(locationTotals.profit_margin || 0).toFixed(1)}%</p>
            </div>
            <div className="rounded-sm border border-white/80 bg-white p-2">
              <p className={ERP_LABEL}>{t('financialAnalysis.status')}</p>
              <p className={cn('mt-1 text-[10px] font-semibold uppercase tracking-[0.12em] leading-snug', (locationTotals.total_profit || 0) >= 0 ? 'text-emerald-700' : 'text-red-700')}>
                {(locationTotals.total_profit || 0) >= 0 ? t('financialAnalysis.expectedProfit') : t('financialAnalysis.negativeProfit')}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-sm border-l-4 border-l-green-500 shadow-none xl:min-w-0">
          <CardContent className="flex h-full flex-col justify-center gap-1 px-3 py-2.5">
            <p className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{t('financialAnalysis.totalRevenue')}</p>
            <p className={cn('font-bold text-green-600', ERP_VALUE_CLAMP)}>{formatCurrency(locationTotals.total_revenue || 0)}</p>
          </CardContent>
        </Card>

        <Card className="rounded-sm border-l-4 border-l-red-500 shadow-none xl:min-w-0">
          <CardContent className="flex h-full flex-col justify-center gap-1 px-3 py-2.5">
            <p className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{t('financialAnalysis.totalCostExpenses')}</p>
            <p className={cn('font-bold text-red-600', ERP_VALUE_CLAMP)}>{formatCurrency(locationTotals.total_cost || 0)}</p>
          </CardContent>
        </Card>

        <Card className="rounded-sm border-l-4 border-l-blue-500 shadow-none xl:min-w-0">
          <CardContent className="flex h-full flex-col justify-center gap-1 px-3 py-2.5">
            <p className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{t('financialAnalysis.operationalProfit')}</p>
            <p className={cn('font-bold', ERP_VALUE_CLAMP, (locationTotals.total_profit || 0) >= 0 ? 'text-green-600' : 'text-red-600')}>
              {formatCurrency(locationTotals.total_profit || 0)}
            </p>
          </CardContent>
        </Card>

        <Card className="rounded-sm border-l-4 border-l-amber-500 shadow-none xl:min-w-0">
          <CardContent className="flex h-full flex-col justify-center gap-1 px-3 py-2.5">
            <p className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{t('financialAnalysis.staleStock')}</p>
            <p className={cn('font-bold text-amber-600', ERP_VALUE_CLAMP)}>{formatCurrency(staleStock?.total_stale_value || 0)}</p>
            <p className="text-[10px] text-muted-foreground">{t('financialAnalysis.productsCount', { count: staleStock?.total_stale_products || 0 })}</p>
          </CardContent>
        </Card>

        <Card className="rounded-sm border-l-4 border-l-purple-500 shadow-none xl:min-w-0">
          <CardContent className="flex h-full flex-col justify-center gap-1 px-3 py-2.5">
            <p className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{t('financialAnalysis.tiedCapital')}</p>
            <p className={cn('font-bold text-purple-600', ERP_VALUE_CLAMP)}>{formatCurrency(capitalTied?.summary?.total_value_at_cost || 0)}</p>
            <p className="text-[10px] text-muted-foreground">{t('financialAnalysis.unitsCount', { count: capitalTied?.summary?.total_units || 0 })}</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-2 xl:grid-cols-[1.2fr_0.8fr]">
        <Card className={ERP_CARD} data-testid="financial-detailed-indicators-card">
          <CardHeader className="pb-2 pt-3">
            <CardTitle className="flex items-center gap-1.5 text-sm font-semibold tracking-tight"><PieChart className="h-3.5 w-3.5 text-slate-700" /> {t('financialAnalysis.detailedIndicators')}</CardTitle>
            <CardDescription>
              {t('financialAnalysis.detailedIndicatorsDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-2 md:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-sm border border-slate-200 bg-slate-50 p-2.5" data-testid="financial-indicator-gross-margin">
              <p className={ERP_LABEL}>{t('financialAnalysis.grossMargin')}</p>
              <p className={cn('mt-1 text-sm font-semibold text-slate-900', ERP_NUM)}>{Number(indicatorsCurrent.gross_margin_pct || 0).toFixed(1)}%</p>
              <div className="mt-2">{renderDeltaBadge(indicatorsComparison.gross_margin_change_pts || 0, ' pts')}</div>
            </div>
            <div className="rounded-sm border border-slate-200 bg-slate-50 p-2.5" data-testid="financial-indicator-net-margin">
              <p className={ERP_LABEL}>{t('financialAnalysis.netMargin')}</p>
              <p className={cn('mt-1 text-sm font-semibold text-slate-900', ERP_NUM)}>{Number(indicatorsCurrent.net_margin_pct || 0).toFixed(1)}%</p>
              <div className="mt-2">{renderDeltaBadge(indicatorsComparison.net_margin_change_pts || 0, ' pts')}</div>
            </div>
            <div className="rounded-sm border border-slate-200 bg-slate-50 p-2.5" data-testid="financial-indicator-average-ticket">
              <p className={ERP_LABEL}>{t('financialAnalysis.averageTicket')}</p>
              <p className={cn('mt-1 text-sm font-semibold text-slate-900', ERP_NUM)}>{formatCurrency(indicatorsCurrent.average_ticket || 0)}</p>
              <div className="mt-2">{renderDeltaBadge(indicatorsComparison.average_ticket_change_pct || 0)}</div>
            </div>
            <div className="rounded-sm border border-slate-200 bg-slate-50 p-2.5" data-testid="financial-indicator-expense-ratio">
              <p className={ERP_LABEL}>{t('financialAnalysis.expenseWeight')}</p>
              <p className={cn('mt-1 text-sm font-semibold text-slate-900', ERP_NUM)}>{Number(indicatorsCurrent.expense_ratio_pct || 0).toFixed(1)}%</p>
              <p className="mt-2 text-xs text-slate-500">{t('financialAnalysis.consideredMoves', { count: indicatorsCurrent.transaction_count || 0 })}</p>
            </div>
          </CardContent>
        </Card>

        <Card className={ERP_CARD} data-testid="financial-comparison-card">
          <CardHeader className="pb-1.5 pt-2.5">
            <CardTitle className="flex items-center gap-1.5 text-sm font-semibold tracking-tight"><TrendingUp className="h-3.5 w-3.5 text-slate-700" /> {t('financialAnalysis.quickComparisons')}</CardTitle>
            <CardDescription className="text-[11px] leading-snug">
              {t('financialAnalysis.quickComparisonsDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 pb-2.5">
            <div className="grid gap-2 md:grid-cols-3">
              <div className="rounded-sm border border-slate-200 bg-slate-50 p-2">
                <p className={ERP_LABEL}>{t('financialAnalysis.revenue')}</p>
                <p className={cn('mt-1 font-semibold text-slate-900', ERP_VALUE_CLAMP)}>{formatCurrency(indicatorsCurrent.revenue || 0)}</p>
                <div className="mt-1.5">{renderDeltaBadge(indicatorsComparison.revenue_change_pct || 0)}</div>
              </div>
              <div className="rounded-sm border border-slate-200 bg-slate-50 p-2">
                <p className={ERP_LABEL}>{t('financialAnalysis.netProfit')}</p>
                <p className={cn('mt-1 font-semibold text-slate-900', ERP_VALUE_CLAMP)}>{formatCurrency(indicatorsCurrent.net_profit || 0)}</p>
                <div className="mt-1.5">{renderDeltaBadge(indicatorsComparison.net_profit_change_pct || 0)}</div>
              </div>
              <div className="rounded-sm border border-slate-200 bg-slate-50 p-2">
                <p className={ERP_LABEL}>{t('financialAnalysis.expenses')}</p>
                <p className={cn('mt-1 font-semibold text-slate-900', ERP_VALUE_CLAMP)}>{formatCurrency(indicatorsCurrent.expenses || 0)}</p>
                <div className="mt-1.5">{renderDeltaBadge(indicatorsComparison.expenses_change_pct || 0)}</div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-1.5" data-testid="financial-highlight-badges">
              <Badge className={cn('rounded-sm border px-1.5 py-0 text-[10px]', indicatorsHighlights.profitability_signal === 'healthy' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : indicatorsHighlights.profitability_signal === 'warning' ? 'border-amber-200 bg-amber-50 text-amber-700' : 'border-red-200 bg-red-50 text-red-700')}>
                {t('financialAnalysis.signal')}: {profitabilitySignalLabel}
              </Badge>
              <Badge variant="outline" className="rounded-sm px-1.5 py-0 text-[10px]">{t('financialAnalysis.serviceMix')}: {Number(indicatorsHighlights.service_mix_pct || 0).toFixed(1)}%</Badge>
              <Badge variant="outline" className="rounded-sm px-1.5 py-0 text-[10px]">{t('financialAnalysis.salesMix')}: {Number(indicatorsHighlights.sales_mix_pct || 0).toFixed(1)}%</Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className={ERP_CARD} data-testid="financial-monthly-trend-card">
        <CardHeader className="pb-1.5 pt-2.5">
          <CardTitle className="flex items-center gap-1.5 text-sm font-semibold tracking-tight"><BarChart3 className="h-3.5 w-3.5 text-slate-700" /> {t('financialAnalysis.monthlyTrend')}</CardTitle>
          <CardDescription className="text-[11px] leading-snug">
            {t('financialAnalysis.monthlyTrendDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2.5 pb-2.5">
          <div className="grid gap-2 lg:grid-cols-2">
            <div className="rounded-sm border border-slate-200 bg-slate-50 p-2.5">
              <p className={ERP_LABEL}>{t('financialAnalysis.bestMonth')}</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">{indicatorsHighlights.best_month?.label || t('financialAnalysis.noData')}</p>
              <p className={cn('text-[11px] text-slate-500', ERP_NUM)}>{t('financialAnalysis.netProfit')}: {formatCurrency(indicatorsHighlights.best_month?.net_profit || 0)}</p>
            </div>
            <div className="rounded-sm border border-slate-200 bg-slate-50 p-2.5">
              <p className={ERP_LABEL}>{t('financialAnalysis.watchMonth')}</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">{indicatorsHighlights.worst_month?.label || t('financialAnalysis.noData')}</p>
              <p className={cn('text-[11px] text-slate-500', ERP_NUM)}>{t('financialAnalysis.netProfit')}: {formatCurrency(indicatorsHighlights.worst_month?.net_profit || 0)}</p>
            </div>
          </div>

          <div className="space-y-1.5">
            {monthlyTrend.map((item) => (
              <div key={item.month_key} className="grid gap-2 rounded-sm border border-slate-200 bg-slate-50 px-2.5 py-1.5 lg:grid-cols-[96px_minmax(0,1fr)_124px_124px] lg:items-center" data-testid={`financial-month-row-${item.month_key}`}>
                <div>
                  <p className="text-sm font-medium text-slate-900">{item.label}</p>
                  <p className="text-[10px] text-slate-500">{t('financialAnalysis.netMarginShort')} {Number(item.net_margin_pct || 0).toFixed(1)}%</p>
                </div>
                <div>
                  <div className="h-2 rounded-full bg-slate-200">
                    <div className="h-2 rounded-full bg-slate-900" style={{ width: `${Math.max(((item.revenue || 0) / maxMonthlyRevenue) * 100, item.revenue > 0 ? 8 : 0)}%` }} />
                  </div>
                </div>
                <div>
                  <p className={ERP_LABEL}>{t('financialAnalysis.revenue')}</p>
                  <p className={cn('text-[13px] font-semibold text-slate-900', ERP_NUM)}>{formatCurrency(item.revenue || 0)}</p>
                </div>
                <div>
                  <p className={ERP_LABEL}>{t('financialAnalysis.netProfit')}</p>
                  <p className={cn('text-[13px] font-semibold', ERP_NUM, (item.net_profit || 0) >= 0 ? 'text-emerald-700' : 'text-red-700')}>
                    {formatCurrency(item.net_profit || 0)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className={ERP_CARD} data-testid="financial-expense-pgt-analysis-card">
        <CardHeader className="pb-2 pt-3">
          <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <CardTitle className="flex items-center gap-1.5 text-sm font-semibold tracking-tight"><PieChart className="h-3.5 w-3.5 text-slate-700" /> Leitura PGT das despesas</CardTitle>
              <CardDescription>
                Visão analítica por categoria e subcategoria PGT para apoiar decisão operacional e de investimento sem carregar ruído desnecessário.
              </CardDescription>
            </div>
            <div className="grid gap-2 sm:grid-cols-2 lg:min-w-[24rem]">
              <Select value={expenseCategoryFilter} onValueChange={setExpenseCategoryFilter}>
                <SelectTrigger className="h-8 rounded-sm text-[11px]" data-testid="financial-expense-category-filter">
                  <SelectValue placeholder="Todas as categorias PGT" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as categorias PGT</SelectItem>
                  {topLevelExpenseCategories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>{category.expense_code} · {category.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={expenseSubcategoryFilter} onValueChange={setExpenseSubcategoryFilter}>
                <SelectTrigger className="h-8 rounded-sm text-[11px]" data-testid="financial-expense-subcategory-filter">
                  <SelectValue placeholder="Todas as subcategorias PGT" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as subcategorias PGT</SelectItem>
                  {expenseSubcategoryOptions.map((subcategory) => (
                    <SelectItem key={subcategory.id} value={subcategory.id}>{subcategory.expense_code} · {subcategory.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-2 md:grid-cols-3">
            <div className="rounded-sm border border-slate-200 bg-slate-50 p-2.5" data-testid="financial-pgt-total-amount">
              <p className={ERP_LABEL}>Montante analisado</p>
              <p className={cn('mt-1 text-sm font-semibold text-slate-900', ERP_NUM)}>{formatCurrency(expensePgtAnalytics?.summary?.total_amount || 0)}</p>
              <p className="mt-2 text-xs text-slate-500">{expensePgtAnalytics?.summary?.total_count || 0} despesas consideradas</p>
            </div>
            <div className="rounded-sm border border-slate-200 bg-slate-50 p-2.5" data-testid="financial-pgt-top-category">
              <p className={ERP_LABEL}>Categoria dominante</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">{expensePgtAnalytics?.summary?.top_category?.category_code || '—'}</p>
              <p className="text-[11px] text-slate-600">{expensePgtAnalytics?.summary?.top_category?.category_name || 'Sem dados suficientes'}</p>
            </div>
            <div className="rounded-sm border border-slate-200 bg-slate-50 p-2.5" data-testid="financial-pgt-top-subcategory">
              <p className={ERP_LABEL}>Subcategoria dominante</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">{expensePgtAnalytics?.summary?.top_subcategory?.subcategory_code || '—'}</p>
              <p className="text-[11px] text-slate-600">{expensePgtAnalytics?.summary?.top_subcategory?.subcategory_name || 'Sem dados suficientes'}</p>
            </div>
          </div>

          <div className="grid gap-3 xl:grid-cols-2">
            <div className="rounded-sm border border-slate-200 bg-white p-2.5">
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-sm font-medium text-slate-900">Categorias com maior peso</p>
                <Badge variant="outline" className="rounded-sm px-1.5 py-0 text-[10px]">{expensePgtAnalytics?.by_category?.length || 0}</Badge>
              </div>
              <div className="space-y-1.5">
                {(expensePgtAnalytics?.by_category || []).slice(0, 6).map((item) => (
                  <div key={item.category_id || item.category_code} className={ERP_ROW} data-testid={`financial-pgt-category-row-${item.category_code}`}>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{item.category_code} · {item.category_name}</p>
                      <p className="text-xs text-slate-500">{item.count} despesas · {item.share_pct}%</p>
                    </div>
                    <p className={cn('text-sm font-semibold text-emerald-700', ERP_NUM)}>{formatCurrency(item.total)}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-sm border border-slate-200 bg-white p-2.5">
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-sm font-medium text-slate-900">Subcategorias com maior peso</p>
                <Badge variant="outline" className="rounded-sm px-1.5 py-0 text-[10px]">{expensePgtAnalytics?.by_subcategory?.length || 0}</Badge>
              </div>
              <div className="space-y-1.5">
                {(expensePgtAnalytics?.by_subcategory || []).slice(0, 6).map((item) => (
                  <div key={item.subcategory_id || `${item.category_code}-${item.subcategory_code}`} className={ERP_ROW} data-testid={`financial-pgt-subcategory-row-${item.subcategory_code}`}>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{item.subcategory_code} · {item.subcategory_name}</p>
                      <p className="text-xs text-slate-500">{item.category_code} · {item.category_name} · {item.count} despesas</p>
                    </div>
                    <p className={cn('text-sm font-semibold text-indigo-700', ERP_NUM)}>{formatCurrency(item.total)}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid h-auto w-full grid-cols-2 gap-1 rounded-sm bg-slate-200/70 p-1 sm:grid-cols-3 xl:grid-cols-7">
          <TabsTrigger className="h-7 rounded-sm px-2 text-[10px] font-semibold uppercase tracking-[0.14em] data-[state=active]:bg-white data-[state=active]:shadow-none" value="profit" data-testid="financial-analysis-tab-profit">{t('financialAnalysis.profitTab')}</TabsTrigger>
          <TabsTrigger className="h-7 rounded-sm px-2 text-[10px] font-semibold uppercase tracking-[0.14em] data-[state=active]:bg-white data-[state=active]:shadow-none" value="allocation" data-testid="financial-analysis-tab-allocation">{t('financialAnalysis.allocationTab')}</TabsTrigger>
          <TabsTrigger className="h-7 rounded-sm px-2 text-[10px] font-semibold uppercase tracking-[0.14em] data-[state=active]:bg-white data-[state=active]:shadow-none" value="employees" data-testid="financial-analysis-tab-employees">{t('financialAnalysis.employeesTab')}</TabsTrigger>
          <TabsTrigger className="h-7 rounded-sm px-2 text-[10px] font-semibold uppercase tracking-[0.14em] data-[state=active]:bg-white data-[state=active]:shadow-none" value="locations" data-testid="financial-analysis-tab-locations">{t('financialAnalysis.locationsTab')}</TabsTrigger>
          <TabsTrigger className="h-7 rounded-sm px-2 text-[10px] font-semibold uppercase tracking-[0.14em] data-[state=active]:bg-white data-[state=active]:shadow-none" value="margins" data-testid="financial-analysis-tab-margins">{t('financialAnalysis.marginsTab')}</TabsTrigger>
          <TabsTrigger className="h-7 rounded-sm px-2 text-[10px] font-semibold uppercase tracking-[0.14em] data-[state=active]:bg-white data-[state=active]:shadow-none" value="suppliers" data-testid="financial-analysis-tab-suppliers">{t('financialAnalysis.suppliersTab')}</TabsTrigger>
          <TabsTrigger className="h-7 rounded-sm px-2 text-[10px] font-semibold uppercase tracking-[0.14em] data-[state=active]:bg-white data-[state=active]:shadow-none" value="stock" data-testid="financial-analysis-tab-stock">{t('financialAnalysis.stockTab')}</TabsTrigger>
        </TabsList>

        {/* Profit Tab */}
        <TabsContent value="profit" className="mt-2 space-y-3">
          {/* Services Profit */}
          <Card className={ERP_CARD}>
            <CardHeader className="pb-2 pt-3">
              <CardTitle className="text-sm font-semibold tracking-tight">{t('financialAnalysis.profitByServiceTitle')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                {/* Repairs */}
                <div className="rounded-sm bg-orange-50 p-2.5">
                  <div className="mb-1.5 flex items-center gap-1.5">
                    <Package className="h-3.5 w-3.5 text-orange-600" />
                    <span className="text-sm font-medium">{t('financialAnalysis.moduleRepairs')}</span>
                  </div>
                  <p className={cn('text-sm font-bold', ERP_NUM)}>{formatCurrency(profitByService?.repairs?.profit || 0)}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {t('financialAnalysis.serviceRevenue')}: {formatCurrency(profitByService?.repairs?.revenue || 0)} | 
                    {t('financialAnalysis.partsCost')}: {formatCurrency(profitByService?.repairs?.parts_cost || 0)}
                  </p>
                  <Badge className="mt-1.5 rounded-sm bg-orange-100 px-1.5 py-0 text-[10px] text-orange-700">
                    {t('financialAnalysis.simpleMargin', { value: (profitByService?.repairs?.margin_pct || 0).toFixed(1) })}
                  </Badge>
                </div>

                {/* Gaming */}
                <div className="rounded-sm bg-purple-50 p-2.5">
                  <div className="mb-1.5 flex items-center gap-1.5">
                    <Package className="h-3.5 w-3.5 text-purple-600" />
                    <span className="text-sm font-medium">{t('financialAnalysis.moduleGaming')}</span>
                  </div>
                  <p className={cn('text-sm font-bold', ERP_NUM)}>{formatCurrency(profitByService?.gaming?.profit || 0)}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {t('financialAnalysis.sessionsCount', { count: profitByService?.gaming?.count || 0 })}
                  </p>
                  <Badge className="mt-1.5 rounded-sm bg-purple-100 px-1.5 py-0 text-[10px] text-purple-700">
                    {t('financialAnalysis.simpleMargin', { value: '100.0' })}
                  </Badge>
                </div>

                {/* Papelaria */}
                <div className="rounded-sm bg-blue-50 p-2.5">
                  <div className="mb-1.5 flex items-center gap-1.5">
                    <Package className="h-3.5 w-3.5 text-blue-600" />
                    <span className="text-sm font-medium">{t('financialAnalysis.modulePapelaria')}</span>
                  </div>
                  <p className={cn('text-sm font-bold', ERP_NUM)}>{formatCurrency(profitByService?.papelaria?.profit || 0)}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {t('financialAnalysis.ordersCount', { count: profitByService?.papelaria?.count || 0 })}
                  </p>
                  <Badge className="mt-1.5 rounded-sm bg-blue-100 px-1.5 py-0 text-[10px] text-blue-700">
                    {t('financialAnalysis.simpleMargin', { value: '100.0' })}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Products Profit */}
          <Card className={ERP_CARD}>
            <CardHeader className="pb-2 pt-3">
              <CardTitle className="text-sm font-semibold tracking-tight">{t('financialAnalysis.profitByProductTitle')}</CardTitle>
              <CardDescription>
                {t('financialAnalysis.topProductsSummary', {
                  revenue: formatCurrency(profitByProduct?.totals?.total_revenue || 0),
                  cost: formatCurrency(profitByProduct?.totals?.total_cost || 0),
                  profit: formatCurrency(profitByProduct?.totals?.total_profit || 0),
                })}
              </CardDescription>
              {profitByProduct?.report_confidence === 'partial' && (
                <div className="mt-2 space-y-1" data-testid="financial-by-product-partial-warning">
                  <Badge className="bg-amber-100 text-amber-800 border border-amber-300">
                    {t('financialAnalysis.partialReportTitle')}
                  </Badge>
                  <p className="text-xs text-amber-700">
                    {(profitByProduct?.warnings || []).slice(0, 2).join(' • ') || t('financialAnalysis.partialReportFallback')}
                  </p>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {(profitByProduct?.products || []).slice(0, 10).length === 0 ? (
                <p className="text-center text-muted-foreground py-8">{t('financialAnalysis.noSalesInFilteredPeriod')}</p>
              ) : (
                <div className="space-y-1.5">
                  {(profitByProduct?.products || []).slice(0, 10).map((p, idx) => (
                    <div key={p.product_id} className={ERP_ROW} data-testid={`financial-product-row-${p.product_id}`}>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-bold text-muted-foreground">#{idx + 1}</span>
                        <div>
                          <p className="text-sm font-medium">{p.product_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {t('financialAnalysis.soldCount', { count: p.quantity_sold })}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={cn("text-sm font-bold", ERP_NUM, p.profit >= 0 ? "text-green-600" : "text-red-600")}>
                          {formatCurrency(p.profit)}
                        </p>
                        <Badge className={cn(
                          "mt-1 rounded-sm px-1.5 py-0 text-[10px]",
                          p.margin_pct == null
                            ? "bg-slate-200 text-slate-700"
                            : p.margin_pct >= 30
                              ? "bg-green-100 text-green-700"
                              : p.margin_pct >= 15
                                ? "bg-amber-100 text-amber-700"
                                : "bg-red-100 text-red-700"
                        )}>
                          {p.margin_pct == null ? t('financialAnalysis.marginUnavailable') : t('financialAnalysis.marginValue', { value: Number(p.margin_pct).toFixed(1) })}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="allocation" className="mt-2 space-y-3">
          <Card data-testid="financial-module-allocation-card">
            <CardHeader>
              <CardTitle>{t('financialAnalysis.allocationTitle')}</CardTitle>
              <CardDescription>
                {t('financialAnalysis.allocationDescription')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {moduleAllocationCards.map((module) => (
                  <div key={module.key} className="rounded-2xl border border-slate-200 bg-slate-50 p-4" data-testid={`financial-module-allocation-${module.key}`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium text-slate-900">{module.label}</p>
                      <Badge variant="outline">{module.data?.count || 0}</Badge>
                    </div>
                    <div className="mt-4 space-y-2 text-sm text-slate-600">
                      <div className="flex items-center justify-between"><span>{t('financialAnalysis.revenue')}</span><span className="font-semibold text-slate-900">{formatCurrency(module.data?.revenue || 0)}</span></div>
                      <div className="flex items-center justify-between"><span>{t('financialAnalysis.directCost')}</span><span className="font-semibold text-slate-900">{formatCurrency(module.data?.parts_cost ?? module.data?.cost ?? 0)}</span></div>
                      <div className="flex items-center justify-between"><span>{t('financialAnalysis.allocatedExpenses')}</span><span className="font-semibold text-slate-900">{formatCurrency(module.data?.allocated_expenses || 0)}</span></div>
                      <div className="flex items-center justify-between"><span>{t('financialAnalysis.operationalProfitLabel')}</span><span className={cn('font-semibold', (module.data?.operational_profit || 0) >= 0 ? 'text-emerald-700' : 'text-red-700')}>{formatCurrency(module.data?.operational_profit || 0)}</span></div>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Badge className="bg-slate-900 text-white">{t('financialAnalysis.marginValue', { value: Number(module.data?.margin_pct || 0).toFixed(1) })}</Badge>
                      <Badge variant="outline">{t('financialAnalysis.operationalMarginValue', { value: Number(module.data?.operational_margin_pct || 0).toFixed(1) })}</Badge>
                    </div>
                  </div>
                ))}

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4" data-testid="financial-module-allocation-inventory">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-medium text-slate-900">{t('financialAnalysis.inventoryModule')}</p>
                    <Badge variant="outline">{t('financialAnalysis.capitalLabel')}</Badge>
                  </div>
                  <div className="mt-4 space-y-2 text-sm text-slate-600">
                    <div className="flex items-center justify-between"><span>{t('financialAnalysis.capitalLabel')}</span><span className="font-semibold text-slate-900">{formatCurrency(capitalTied?.total_capital_tied || capitalTied?.summary?.total_value_at_cost || 0)}</span></div>
                    <div className="flex items-center justify-between"><span>{t('financialAnalysis.staleStockItems')}</span><span className="font-semibold text-slate-900">{t('financialAnalysis.itemsLabel', { count: staleStock?.summary?.stale_count || staleStock?.total_stale_products || 0 })}</span></div>
                    <div className="flex items-center justify-between"><span>{t('financialAnalysis.riskValue')}</span><span className="font-semibold text-slate-900">{formatCurrency(staleStock?.summary?.total_stale_value || staleStock?.total_stale_value || 0)}</span></div>
                    <div className="flex items-center justify-between"><span>{t('financialAnalysis.alertsLabel')}</span><span className="font-semibold text-slate-900">{stockAlerts?.summary?.critical_alerts || stockAlertByPriority.critical || 0}</span></div>
                  </div>
                </div>
              </div>

              <div className="grid gap-4 lg:grid-cols-[1fr_0.8fr]">
                <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="financial-expense-allocation-breakdown">
                  <p className="text-sm font-medium text-slate-900">{t('financialAnalysis.expenseAllocationTitle')}</p>
                  <p className="mt-1 text-xs text-slate-500">{profitByService?.expense_allocation?.allocation_rule || t('financialAnalysis.noAllocationRule')}</p>
                  <div className="mt-4 space-y-2">
                    {Object.entries(profitByService?.expense_allocation?.allocated_total_expenses || {}).map(([moduleKey, amount]) => (
                      <div key={moduleKey} className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                        <span className="text-slate-600">{moduleAllocationCards.find((card) => card.key === moduleKey)?.label || moduleKey}</span>
                        <span className="font-semibold text-slate-900">{formatCurrency(amount || 0)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="financial-allocation-totals-card">
                  <p className="text-sm font-medium text-slate-900">{t('financialAnalysis.allocatedTotalsTitle')}</p>
                  <div className="mt-4 space-y-3 text-sm">
                    <div className="flex items-center justify-between"><span className="text-slate-500">{t('financialAnalysis.totalRevenueLabel')}</span><span className="font-semibold text-slate-900">{formatCurrency(profitByService?.totals?.total_revenue || 0)}</span></div>
                    <div className="flex items-center justify-between"><span className="text-slate-500">{t('financialAnalysis.totalCostLabel')}</span><span className="font-semibold text-slate-900">{formatCurrency(profitByService?.totals?.total_cost || 0)}</span></div>
                    <div className="flex items-center justify-between"><span className="text-slate-500">{t('financialAnalysis.grossProfitLabel')}</span><span className="font-semibold text-slate-900">{formatCurrency(profitByService?.totals?.total_profit || 0)}</span></div>
                    <div className="flex items-center justify-between"><span className="text-slate-500">{t('financialAnalysis.operationalProfitLabel')}</span><span className="font-semibold text-slate-900">{formatCurrency(profitByService?.totals?.total_operational_profit || 0)}</span></div>
                    <div className="flex items-center justify-between"><span className="text-slate-500">{t('financialAnalysis.allocatedExpenses')}</span><span className="font-semibold text-slate-900">{formatCurrency(profitByService?.totals?.total_allocated_expenses || 0)}</span></div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Employees Tab */}
        <TabsContent value="employees" className="mt-2 space-y-3">
          <Card className={ERP_CARD}>
            <CardHeader className="pb-2 pt-3">
              <CardTitle className="flex items-center gap-1.5 text-sm font-semibold tracking-tight">
                <Users className="h-3.5 w-3.5 text-blue-600" />
                {t('financialAnalysis.employeesPerformanceTitle')}
              </CardTitle>
              <CardDescription>
                {t('financialAnalysis.employeesPerformanceSummary', {
                  profit: formatCurrency(profitByEmployee?.totals?.total_profit || 0),
                  sales: profitByEmployee?.totals?.total_sales || 0,
                  services: profitByEmployee?.totals?.total_services || 0,
                })}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {(profitByEmployee?.employees || []).length === 0 ? (
                <p className="text-center text-muted-foreground py-8">{t('financialAnalysis.noActivityInPeriod')}</p>
              ) : (
                <div className="space-y-1.5">
                  {(profitByEmployee?.employees || []).map((emp, idx) => (
                    <div key={emp.employee_id || idx} className={cn(
                      "flex items-center justify-between rounded-sm border px-2.5 py-1.5",
                      idx === 0 && "border-green-300 bg-green-50",
                      idx === (profitByEmployee?.employees?.length || 0) - 1 && profitByEmployee?.employees?.length > 1 && "border-red-300 bg-red-50"
                    )} data-testid={`financial-employee-row-${emp.employee_id || idx}`}>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-bold text-muted-foreground">#{idx + 1}</span>
                        <div>
                          <p className="text-sm font-medium">{emp.employee_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {t('financialAnalysis.salesAndServicesCount', { sales: emp.sales_count, services: emp.services_count })}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={cn("text-sm font-bold", ERP_NUM, emp.total_profit >= 0 ? "text-green-600" : "text-red-600")}>
                          {formatCurrency(emp.total_profit)}
                        </p>
                        <div className="mt-1 flex gap-1.5">
                          <Badge variant="outline" className="rounded-sm px-1.5 py-0 text-[10px]">
                            {t('financialAnalysis.salesRevenue', { value: formatCurrency(emp.sales_revenue) })}
                          </Badge>
                          <Badge variant="outline" className="rounded-sm px-1.5 py-0 text-[10px]">
                            {t('financialAnalysis.servicesRevenue', { value: formatCurrency(emp.services_revenue) })}
                          </Badge>
                        </div>
                        <Badge className={cn(
                          "mt-1 rounded-sm px-1.5 py-0 text-[10px]",
                          emp.margin_pct >= 30 ? "bg-green-100 text-green-700" :
                          emp.margin_pct >= 15 ? "bg-amber-100 text-amber-700" :
                          "bg-red-100 text-red-700"
                        )}>
                          {t('financialAnalysis.marginValue', { value: (emp.margin_pct || 0).toFixed(1) })}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {profitByEmployee?.top_performer && profitByEmployee?.bottom_performer && profitByEmployee?.performance_gap > 0 && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <p className="font-medium text-blue-800">{t('financialAnalysis.performanceAnalysisTitle')}</p>
                  <p className="text-sm text-blue-700">
                    • {t('financialAnalysis.bestPerformerLabel')} <b>{profitByEmployee.top_performer.employee_name}</b> ({formatCurrency(profitByEmployee.top_performer.total_profit)})
                  </p>
                  <p className="text-sm text-blue-700">
                    • {t('financialAnalysis.performanceGapLabel')} <b>{formatCurrency(profitByEmployee.performance_gap)}</b>
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Locations Tab */}
        <TabsContent value="locations" className="mt-2 space-y-3">
          <Card className={ERP_CARD}>
            <CardHeader className="pb-2 pt-3">
              <CardTitle className="text-sm font-semibold tracking-tight">{t('financialAnalysis.storePerformanceTitle')}</CardTitle>
              {(profitByLocation?.warnings?.draining_locations || []).length > 0 && (
                <div className="flex items-center gap-2 text-red-600 text-sm">
                  <AlertTriangle className="h-4 w-4" />
                  {t('financialAnalysis.drainingLocations', { locations: profitByLocation.warnings.draining_locations.join(', ') })}
                </div>
              )}
            </CardHeader>
            <CardContent>
              {(profitByLocation?.locations || []).length === 0 ? (
                <p className="text-center text-muted-foreground py-8">{t('financialAnalysis.noStoresFound')}</p>
              ) : (
                <div className="space-y-2">
                  {(profitByLocation?.locations || []).map(loc => (
                    <div key={loc.location_id} className={cn(
                      "rounded-sm border px-2.5 py-2",
                      !loc.is_profitable && "border-red-300 bg-red-50"
                    )} data-testid={`financial-location-row-${loc.location_id}`}>
                      <div className="mb-2 flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <Store className={cn("h-3.5 w-3.5", loc.is_profitable ? "text-green-600" : "text-red-600")} />
                          <div>
                            <p className="text-sm font-medium">{loc.location_name}</p>
                            <Badge variant="outline" className="rounded-sm px-1.5 py-0 text-[10px]">{loc.location_type}</Badge>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={cn("text-sm font-bold", ERP_NUM, loc.is_profitable ? "text-green-600" : "text-red-600")}>
                            {formatCurrency(loc.profit)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {t('financialAnalysis.simpleMargin', { value: loc.margin_pct.toFixed(1) })}
                          </p>
                        </div>
                      </div>
                      
                      <div className="grid gap-2 text-[11px] sm:grid-cols-2 xl:grid-cols-4">
                        <div>
                          <p className="text-xs text-muted-foreground">{t('financialAnalysis.salesShort')}</p>
                          <p className="font-medium">{formatCurrency(loc.sales_revenue)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">{t('financialAnalysis.servicesShort')}</p>
                          <p className="font-medium">{formatCurrency(loc.services_revenue)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">{t('financialAnalysis.moduleGaming')}</p>
                          <p className="font-medium">{formatCurrency(loc.gaming_revenue)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">{t('financialAnalysis.expensesShort')}</p>
                          <p className="font-medium text-red-600">{formatCurrency(loc.expenses)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Margins Tab */}
        <TabsContent value="margins" className="mt-2 space-y-3">
          <Card className={ERP_CARD}>
            <CardHeader className="pb-2 pt-3">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-purple-600" />
                    {t('financialAnalysis.expectedVsActualMarginTitle')}
                  </CardTitle>
                  <CardDescription>
                    {t('financialAnalysis.gapSummary', {
                      amount: formatCurrency(marginAnalysis?.summary?.profit_gap || 0),
                      percent: (marginAnalysis?.summary?.gap_percentage || 0).toFixed(1),
                    })}
                  </CardDescription>
                </div>
                <div className="rounded-sm border border-slate-200 bg-slate-50 p-2.5 lg:min-w-[240px]" data-testid="financial-analysis-minimum-margin-config-card">
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{t('financialAnalysis.reportInfoTitle')}</p>
                  <div className="mt-3 space-y-2">
                    <label htmlFor="financial-analysis-minimum-margin-input" className="text-sm font-medium text-slate-800">
                      {t('financialAnalysis.minimumRequiredMarginLabel')}
                    </label>
                    <Input
                      id="financial-analysis-minimum-margin-input"
                      type="number"
                      min="0"
                      max="100"
                      step="0.5"
                      value={minimumMarginPct}
                      onChange={(e) => setMinimumMarginPct(Number(e.target.value || 0))}
                      data-testid="financial-analysis-minimum-margin-input"
                    />
                    <p className="text-xs text-slate-500">{t('financialAnalysis.minimumRequiredMarginHint')}</p>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-3 grid gap-2 md:grid-cols-3">
                <div className="rounded-sm bg-blue-50 p-2 text-center">
                  <p className="text-xs text-muted-foreground">{t('financialAnalysis.expectedProfitLabel')}</p>
                  <p className={cn('text-sm font-bold text-blue-600', ERP_NUM)}>
                    {formatCurrency(marginAnalysis?.summary?.total_expected_profit || 0)}
                  </p>
                </div>
                <div className="rounded-sm bg-green-50 p-2 text-center">
                  <p className="text-xs text-muted-foreground">{t('financialAnalysis.realProfitLabel')}</p>
                  <p className={cn('text-sm font-bold text-green-600', ERP_NUM)}>
                    {formatCurrency(marginAnalysis?.summary?.total_actual_profit || 0)}
                  </p>
                </div>
                <div className={cn("rounded-sm p-2 text-center", 
                  marginAnalysis?.summary?.profit_gap >= 0 ? "bg-green-50" : "bg-red-50"
                )}>
                  <p className="text-xs text-muted-foreground">{t('financialAnalysis.differenceLabel')}</p>
                  <p className={cn("text-sm font-bold", ERP_NUM,
                    marginAnalysis?.summary?.profit_gap >= 0 ? "text-green-600" : "text-red-600"
                  )}>
                    {formatCurrency(marginAnalysis?.summary?.profit_gap || 0)}
                  </p>
                </div>
              </div>

              <div className="mb-4 rounded-2xl border border-violet-200 bg-violet-50 px-4 py-3 text-sm text-violet-900" data-testid="financial-analysis-minimum-margin-info">
                {t('financialAnalysis.currentMinimumMargin', { value: (marginAnalysis?.configuration?.minimum_margin_pct ?? minimumMarginPct).toFixed(1) })}
              </div>
              
              {marginAnalysis?.underperforming_count > 0 && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
                  <p className="font-medium text-amber-800 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    {t('financialAnalysis.underperformingProducts', { count: marginAnalysis.underperforming_count })}
                  </p>
                </div>
              )}
              
              <div className="space-y-1.5">
                <p className="text-sm font-medium text-muted-foreground">{t('financialAnalysis.productsMarginDeviation')}</p>
                {(marginAnalysis?.products || []).slice(0, 10).map(p => (
                  <div key={p.product_id} className={cn(
                    "flex items-center justify-between rounded-sm border px-2.5 py-1.5",
                    p.is_underperforming && "border-red-300 bg-red-50"
                  )} data-testid={`financial-margin-row-${p.product_id}`}>
                    <div>
                      <p className="font-medium text-sm">{p.product_name}</p>
                      <p className="text-xs text-muted-foreground">{t('financialAnalysis.soldCount', { count: p.quantity_sold })}</p>
                    </div>
                    <div className="text-right">
                      <div className="flex gap-2 text-xs">
                        <span className="text-muted-foreground">{t('financialAnalysis.expectedLabel', { value: p.expected_margin_pct })}</span>
                        <span className={cn(
                          p.actual_margin_pct >= p.expected_margin_pct ? "text-green-600" : "text-red-600"
                        )}>{t('financialAnalysis.actualLabel', { value: p.actual_margin_pct })}</span>
                      </div>
                      <Badge className={cn("text-xs",
                        p.margin_deviation >= 0 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                      )}>
                        {t('financialAnalysis.deviationLabel', { value: `${p.margin_deviation >= 0 ? '+' : ''}${p.margin_deviation}` })}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Suppliers Tab */}
        <TabsContent value="suppliers" className="mt-2 space-y-3">
          <Card className={ERP_CARD}>
            <CardHeader className="pb-2 pt-3">
              <CardTitle className="text-sm font-semibold tracking-tight">{t('financialAnalysis.supplierComparisonTitle')}</CardTitle>
              <CardDescription>{t('financialAnalysis.supplierComparisonDescription')}</CardDescription>
            </CardHeader>
            <CardContent>
              {supplierComparison.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">{t('financialAnalysis.noSuppliersFound')}</p>
              ) : (
                <div className="space-y-1.5">
                  {supplierComparison.map((s, idx) => (
                    <div key={s.supplier_id} className={ERP_ROW} data-testid={`financial-supplier-row-${s.supplier_id}`}>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-bold text-muted-foreground">#{idx + 1}</span>
                        <Truck className="h-3.5 w-3.5 text-blue-500" />
                        <div>
                          <p className="font-medium">{s.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {t('financialAnalysis.ordersAndProductsCount', { orders: s.total_orders, products: s.unique_products })}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={cn('text-sm font-bold', ERP_NUM)}>{formatCurrency(s.total_value)}</p>
                        <p className="text-xs text-muted-foreground">
                          {t('financialAnalysis.averageOrderValue', { value: formatCurrency(s.avg_order_value) })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Stock Tab */}
        <TabsContent value="stock" className="mt-2 space-y-3">
          {/* Stock Alerts */}
          {stockAlerts && stockAlertTotal > 0 && (
            <Card className="rounded-sm border-amber-300 shadow-none">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-amber-600">
                  <AlertTriangle className="h-5 w-5" />
                  {t('financialAnalysis.stockAlertsTitle', { count: stockAlertTotal })}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-2 flex gap-2">
                  <Badge className="rounded-sm bg-red-100 px-1.5 py-0 text-[10px] text-red-700">{t('financialAnalysis.criticalCount', { count: stockAlertByPriority.critical || 0 })}</Badge>
                  <Badge className="rounded-sm bg-orange-100 px-1.5 py-0 text-[10px] text-orange-700">{t('financialAnalysis.highCount', { count: stockAlertByPriority.high || 0 })}</Badge>
                  <Badge className="rounded-sm bg-amber-100 px-1.5 py-0 text-[10px] text-amber-700">{t('financialAnalysis.mediumCount', { count: stockAlertByPriority.medium || 0 })}</Badge>
                </div>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {(stockAlerts.alerts || []).slice(0, 5).map((a, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm p-2 bg-amber-50 rounded">
                      <Badge className={cn(
                        "text-xs",
                        a.priority === 'critical' ? "bg-red-100 text-red-700" :
                        a.priority === 'high' ? "bg-orange-100 text-orange-700" :
                        "bg-amber-100 text-amber-700"
                      )}>
                        {a.type}
                      </Badge>
                      <span>{a.message}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Stock Rotation */}
          <div className="grid gap-3 xl:grid-cols-2">
            <Card className={ERP_CARD}>
              <CardHeader className="pb-2 pt-3">
                <CardTitle className="flex items-center gap-1.5 text-sm font-semibold tracking-tight">
                  <TrendingUp className="h-3.5 w-3.5 text-green-600" />
                  {t('financialAnalysis.highRotationTitle', { count: stockRotation?.high_rotation_count || 0 })}
                </CardTitle>
                <CardDescription>{t('financialAnalysis.highRotationDescription')}</CardDescription>
              </CardHeader>
              <CardContent>
                {(stockRotation?.high_rotation_products || []).length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">{t('financialAnalysis.noProductsLabel')}</p>
                ) : (
                  <div className="space-y-1.5">
                    {(stockRotation?.high_rotation_products || []).map(p => (
                      <div key={p.product_id} className="flex items-center justify-between rounded-sm bg-green-50 px-2 py-1.5" data-testid={`financial-stock-high-row-${p.product_id}`}>
                        <div>
                          <p className="font-medium text-sm">{p.product_name}</p>
                          <p className="text-xs text-muted-foreground">{t('financialAnalysis.soldCount', { count: p.quantity_sold })}</p>
                        </div>
                        <Badge className="rounded-sm bg-green-100 px-1.5 py-0 text-[10px] text-green-700">
                          {p.rotation_rate}x
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className={ERP_CARD}>
              <CardHeader className="pb-2 pt-3">
                <CardTitle className="flex items-center gap-1.5 text-sm font-semibold tracking-tight">
                  <TrendingDown className="h-3.5 w-3.5 text-red-600" />
                  {t('financialAnalysis.lowRotationTitle', { count: stockRotation?.low_rotation_count || 0 })}
                </CardTitle>
                <CardDescription>{t('financialAnalysis.lowRotationDescription')}</CardDescription>
              </CardHeader>
              <CardContent>
                {(stockRotation?.low_rotation_products || []).length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">{t('financialAnalysis.noProductsLabel')}</p>
                ) : (
                  <div className="space-y-1.5">
                    {(stockRotation?.low_rotation_products || []).map(p => (
                      <div key={p.product_id} className="flex items-center justify-between rounded-sm bg-red-50 px-2 py-1.5" data-testid={`financial-stock-low-row-${p.product_id}`}>
                        <div>
                          <p className="font-medium text-sm">{p.product_name}</p>
                          <p className="text-xs text-muted-foreground">{t('financialAnalysis.stockShort', { count: p.current_stock })}</p>
                        </div>
                        <Badge className="rounded-sm bg-red-100 px-1.5 py-0 text-[10px] text-red-700">
                          {p.rotation_rate}x
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Stale Stock */}
          <Card className={ERP_CARD}>
            <CardHeader className="pb-2 pt-3">
              <CardTitle className="flex items-center gap-1.5 text-sm font-semibold tracking-tight">
                <Clock className="h-3.5 w-3.5 text-amber-600" />
                {t('financialAnalysis.staleProductsTitle')}
              </CardTitle>
              <CardDescription>
                {t('financialAnalysis.staleProductsDescription', {
                  count: staleStock?.total_stale_products || 0,
                  value: formatCurrency(staleStock?.total_stale_value || 0),
                })}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {(staleStock?.stale_products || []).length === 0 ? (
                <p className="text-center text-muted-foreground py-8">{t('financialAnalysis.noStaleProducts')}</p>
              ) : (
                <div className="max-h-60 space-y-1.5 overflow-y-auto">
                  {(staleStock?.stale_products || []).slice(0, 15).map(p => (
                    <div key={p.product_id} className="flex items-center justify-between rounded-sm bg-amber-50 px-2.5 py-1.5" data-testid={`financial-stale-row-${p.product_id}`}>
                      <div>
                        <p className="font-medium">{p.product_name}</p>
                        <p className="text-xs text-muted-foreground">
                          SKU: {p.sku} | {t('financialAnalysis.stockShort', { count: p.current_stock })} | 
                          {t('financialAnalysis.lastSaleLabel', { value: p.days_since_sale === 'Never sold' ? t('financialAnalysis.neverSold') : t('financialAnalysis.daysAgo', { count: p.days_since_sale }) })}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className={cn('text-sm font-bold text-amber-600', ERP_NUM)}>{formatCurrency(p.stock_value)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {(staleStock?.recommendations || []).length > 0 && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <p className="font-medium text-blue-800 mb-1">{t('financialAnalysis.recommendationsTitle')}</p>
                  {staleStock.recommendations.map((r, idx) => (
                    <p key={idx} className="text-sm text-blue-700">• {r}</p>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
    </ModuleStateBoundary>
  );
}
